import type {
  HazardIdentification,
  RiskReductionMeasure,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import type { EhsrClause } from '../../ehsr/types'
import {
  buildEhsrHierarchyTree,
  type EhsrHierarchyNode,
} from './ehsrHierarchy'

export type CoverageCount = {
  covered: number
  total: number
}

export type LinkedHazardSummary = {
  id: string
  type: string | null
  description: string | null
}

export type LinkedRrmSummary = {
  id: string
  name: string
  type: RiskReductionMeasure['type']
}

export type EhsrOverviewNode = {
  nodeId: string
  kind: 'group' | 'leaf'
  title: string
  selectedLabel?: string
  clause?: EhsrClause
  linkedHazards: LinkedHazardSummary[]
  linkedRrms: LinkedRrmSummary[]
  hazardCovered: boolean
  rrmCovered: boolean
  hazardCoverage: CoverageCount
  rrmCoverage: CoverageCount
  children: EhsrOverviewNode[]
}

export function normalizeEhsrClauseIds(
  clauseIds: string[] | null | undefined
): string[] {
  if (!Array.isArray(clauseIds)) {
    return []
  }

  return clauseIds
    .map((clauseId) => clauseId.trim())
    .filter((clauseId) => clauseId.length > 0)
}

function isAppliedRiskReductionMeasure(
  measure: RiskReductionMeasure
): boolean {
  return (measure.linkedHazards?.length ?? 0) > 0
}

function buildHazardLinksByClause(
  hazards: HazardIdentification[]
): Map<string, LinkedHazardSummary[]> {
  const linksByClause = new Map<string, LinkedHazardSummary[]>()

  for (const hazard of hazards) {
    const clauseIds = normalizeEhsrClauseIds(hazard.ehsrClauseIds)
    const summary: LinkedHazardSummary = {
      id: hazard.id,
      type: hazard.type,
      description: hazard.description,
    }

    for (const clauseId of clauseIds) {
      const existing = linksByClause.get(clauseId) ?? []
      existing.push(summary)
      linksByClause.set(clauseId, existing)
    }
  }

  return linksByClause
}

function buildAppliedRrmLinksByClause(
  measures: RiskReductionMeasure[]
): Map<string, LinkedRrmSummary[]> {
  const linksByClause = new Map<string, LinkedRrmSummary[]>()

  for (const measure of measures) {
    if (!isAppliedRiskReductionMeasure(measure)) {
      continue
    }

    const clauseIds = normalizeEhsrClauseIds(measure.ehsrClauseIds)
    const summary: LinkedRrmSummary = {
      id: measure.id,
      name: measure.name,
      type: measure.type,
    }

    for (const clauseId of clauseIds) {
      const existing = linksByClause.get(clauseId) ?? []
      existing.push(summary)
      linksByClause.set(clauseId, existing)
    }
  }

  return linksByClause
}

function decorateHierarchyNode(
  node: EhsrHierarchyNode,
  hazardLinksByClause: Map<string, LinkedHazardSummary[]>,
  rrmLinksByClause: Map<string, LinkedRrmSummary[]>
): EhsrOverviewNode {
  const linkedHazards =
    node.kind === 'leaf' ? (hazardLinksByClause.get(node.nodeId) ?? []) : []
  const linkedRrms =
    node.kind === 'leaf' ? (rrmLinksByClause.get(node.nodeId) ?? []) : []

  const children = node.children.map((child) =>
    decorateHierarchyNode(child, hazardLinksByClause, rrmLinksByClause)
  )

  const hazardCovered = linkedHazards.length > 0
  const rrmCovered = linkedRrms.length > 0

  let hazardCoverage: CoverageCount = { covered: 0, total: 0 }
  let rrmCoverage: CoverageCount = { covered: 0, total: 0 }

  if (node.kind === 'leaf') {
    hazardCoverage = {
      covered: linkedHazards.length,
      total: 1,
    }
    rrmCoverage = {
      covered: linkedRrms.length,
      total: 1,
    }
  } else {
    for (const child of children) {
      hazardCoverage.covered += child.hazardCoverage.covered
      hazardCoverage.total += child.hazardCoverage.total
      rrmCoverage.covered += child.rrmCoverage.covered
      rrmCoverage.total += child.rrmCoverage.total
    }
  }

  return {
    nodeId: node.nodeId,
    kind: node.kind,
    title: node.title,
    selectedLabel: node.clause?.selectedLabel,
    clause: node.clause,
    linkedHazards,
    linkedRrms,
    hazardCovered,
    rrmCovered,
    hazardCoverage,
    rrmCoverage,
    children,
  }
}

export function buildEhsrOverviewModel(input: {
  clauses: EhsrClause[]
  hazards: HazardIdentification[]
  measures: RiskReductionMeasure[]
}): EhsrOverviewNode[] {
  const hierarchy = buildEhsrHierarchyTree(input.clauses)
  const hazardLinksByClause = buildHazardLinksByClause(input.hazards)
  const rrmLinksByClause = buildAppliedRrmLinksByClause(input.measures)

  return hierarchy.map((node) =>
    decorateHierarchyNode(node, hazardLinksByClause, rrmLinksByClause)
  )
}
